import { useState } from 'react'
import styled from 'styled-components'
import { useQuery } from '@tanstack/react-query'
import useApi from '../../../hooks/useApi'
import useTranslation from '../../../hooks/useTranslation'
import Drawer from '../../drawer/Drawer'
import FolderPicker from './FolderPicker'
import { Icon_Document } from '../../../styles/icons'

const Layout = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  overflow: hidden;
`

const FolderPanel = styled.div`
  width: 220px;
  flex-shrink: 0;
  border-right: 1px solid ${({ theme }) => theme.border?.default || '#e5e7eb'};
  display: flex;
  flex-direction: column;
  overflow: hidden;
`

const DocPanel = styled.div`
  flex: 1 1 auto;
  overflow-y: auto;
  padding: 0.5rem 0.25rem;
`

const DocRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.875rem;
  color: ${({ theme }) => theme.font?.label || '#374151'};

  &:hover {
    background: ${({ theme }) => theme.dropdown?.hoverBackground || theme.area?.highBg || '#f3f4f6'};
  }

  svg { font-size: 1rem; flex-shrink: 0; opacity: 0.65; }
`

const DocName = styled.span`
  flex: 1;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const DocMeta = styled.span`
  font-size: 0.75rem;
  color: ${({ theme }) => theme.font?.subtle || '#9ca3af'};
  white-space: nowrap;
`

const EmptyMsg = styled.p`
  text-align: center;
  color: ${({ theme }) => theme.font?.subtle || '#9ca3af'};
  font-size: 0.875rem;
  padding: 2rem 1rem;
  margin: 0;
`

const DocumentPickerDrawer = ({ isOpen, onClose, onSelect, title }) => {
  const { getMe } = useApi()
  const { t, m } = useTranslation()
  const [selectedFolder, setSelectedFolder] = useState(null)

  const queryParam = selectedFolder?.id
    ? `document?noPagination=true&orderBy=name&folderId=${selectedFolder.id}`
    : `document?noPagination=true&orderBy=name`

  const { data, isLoading } = useQuery({
    queryKey: ['document', 'picker', selectedFolder?.id ?? 'all'],
    queryFn: () => getMe(queryParam),
    staleTime: 30000,
    enabled: isOpen,
  })

  const documents = data?.data ?? []

  const handleSelect = (doc) => {
    onSelect(doc)
    onClose()
  }

  return (
    <Drawer isOpen={isOpen} onClose={onClose} title={title || t('document.picker.drawer.title')} width="680px">
      <Drawer.Body $padding="0">
        <Layout>
          <FolderPanel>
            <FolderPicker
              selectedId={selectedFolder?.id ?? null}
              onSelect={setSelectedFolder}
            />
          </FolderPanel>
          <DocPanel>
            {isLoading ? (
              <EmptyMsg>{t('document.picker.loading')}</EmptyMsg>
            ) : documents.length === 0 ? (
              <EmptyMsg>
                {selectedFolder
                  ? m('document.picker.empty.folder', [selectedFolder.name])
                  : t('document.picker.empty')}
              </EmptyMsg>
            ) : (
              documents.map(doc => (
                <DocRow key={doc.id} onClick={() => handleSelect(doc)}>
                  {Icon_Document}
                  <DocName>{doc.name}</DocName>
                  {doc.fileTypeName && <DocMeta>{doc.fileTypeName}</DocMeta>}
                </DocRow>
              ))
            )}
          </DocPanel>
        </Layout>
      </Drawer.Body>
    </Drawer>
  )
}

export default DocumentPickerDrawer
